import { AxiosError } from 'axios'
import { useCallback } from 'react'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { Area } from '@/interfaces/area'
import { BillingRegister } from '@/interfaces/billing'

import APIServices from '../../api/APIServices'

export interface InvoiceQueryParams {
  per_page?: number | null
  page?: number | null
  status?: string | null
  billFrom?: string | null
  billTo?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  origin?: string | null
  search?: string | null
  deliveryAt?: string | null
  coupons?: string | null
  productId?: string | null
  paymentStatus?: string | null
  isPreReceipt?: boolean | null
  paymentDeadlineFrom?: string | null
  paymentDeadlineTo?: string | null
  provinceId?: string | null
  municipalityId?: string | null
  includeShipping?: boolean | null
}

export interface AreaQueryParams {
  per_page?: number | null
  page?: number | null
  isActive?: string | null
  type?: string | null
  name?: string | null
  search?: string | null
}

export interface InvoiceResponse {
  totalItems: number
  currentPage: number
  totalPages: number
  items: BillingRegister[]
}

export interface AreaResponse {
  totalItems: number
  currentPage: number
  totalPages: number
  items: Area[]
}

// Create a unique, descriptive key for this query
const INVOICE_QUERY_KEY = 'invoices'
const AREA_QUERY_KEY = 'areas'

/**
 * Fetch invoices from the API
 * Separating API call logic from the hook for better testability
 */
const fetchInvoices = async (
  params?: InvoiceQueryParams
): Promise<InvoiceResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/invoice', {
    params: cleanParams,
  })
  return response.data
}

const fetchAreas = async (params?: AreaQueryParams): Promise<AreaResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await APIServices.get('/administration/area', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching invoices with filtering
 * This hook now properly uses external parameters without internal state conflicts
 *
 * @param params - Query parameters for filtering invoices
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useInvoiceQuery = ({
  params = {},
  options,
  businessId,
}: {
  params?: InvoiceQueryParams
  options?: Partial<UseQueryOptions<InvoiceResponse, AxiosError>>
  businessId: number
}) => {
  // Normalize params with defaults
  const normalizedParams: InvoiceQueryParams = {
    page: 1,
    per_page: 10,
    ...params,
  }

  // Query hook - now properly reactive to external parameter changes
  const queryResult = useQuery<InvoiceResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    // This ensures the query refetches when parameters change
    queryKey: [INVOICE_QUERY_KEY, normalizedParams, businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchInvoices(normalizedParams),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  // Helper functions for pagination metadata
  const getCurrentPage = useCallback(() => {
    return normalizedParams.page || 1
  }, [normalizedParams.page])

  const getPageSize = useCallback(() => {
    return normalizedParams.per_page || 10
  }, [normalizedParams.per_page])

  const hasNextPage = useCallback(() => {
    if (!queryResult.data) return false
    const { currentPage, totalPages } = queryResult.data
    return currentPage < totalPages
  }, [queryResult.data])

  const hasPreviousPage = useCallback(() => {
    const currentPage = getCurrentPage()
    return currentPage > 1
  }, [getCurrentPage])

  return {
    ...queryResult,
    // Add pagination helpers and metadata
    pagination: {
      currentPage: getCurrentPage(),
      pageSize: getPageSize(),
      hasNextPage: hasNextPage(),
      hasPreviousPage: hasPreviousPage(),
      totalPages: queryResult.data?.totalPages || 0,
      totalItems: queryResult.data?.totalItems || 0,
      params: normalizedParams,
    },
  }
}

export const useAreaQuery = ({
  params = {},
  options,
  businessId,
}: {
  params?: AreaQueryParams
  options?: Partial<UseQueryOptions<AreaResponse, AxiosError>>
  businessId: number
}) => {
  // Normalize params with defaults
  const normalizedParams: AreaQueryParams = {
    page: 1,
    per_page: 10,
    ...params,
  }

  // Query hook - now properly reactive to external parameter changes
  const queryResult = useQuery<AreaResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    // This ensures the query refetches when parameters change
    queryKey: [AREA_QUERY_KEY, normalizedParams, businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchAreas(normalizedParams),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  // Helper functions for pagination metadata
  const getCurrentPage = useCallback(() => {
    return normalizedParams.page || 1
  }, [normalizedParams.page])

  const getPageSize = useCallback(() => {
    return normalizedParams.per_page || 10
  }, [normalizedParams.per_page])

  const hasNextPage = useCallback(() => {
    if (!queryResult.data) return false
    const { currentPage, totalPages } = queryResult.data
    return currentPage < totalPages
  }, [queryResult.data])

  const hasPreviousPage = useCallback(() => {
    const currentPage = getCurrentPage()
    return currentPage > 1
  }, [getCurrentPage])

  return {
    ...queryResult,
    // Add pagination helpers and metadata
    pagination: {
      currentPage: getCurrentPage(),
      pageSize: getPageSize(),
      hasNextPage: hasNextPage(),
      hasPreviousPage: hasPreviousPage(),
      totalPages: queryResult.data?.totalPages || 0,
      totalItems: queryResult.data?.totalItems || 0,
      params: normalizedParams,
    },
  }
}
/**
 * Helper function to get the invoice query key
 * Useful for cache invalidation or prefetching
 */
export const getInvoiceQueryKey = (params?: InvoiceQueryParams) => [
  INVOICE_QUERY_KEY,
  params,
]

export const getAreaQueryKey = (params?: AreaQueryParams) => [
  AREA_QUERY_KEY,
  params,
]
